import { useState, useEffect, type ReactNode } from 'react';

type Mode = 'default' | 'checkbox';

export interface InlineEditableCellProps<T extends ReactNode> {
  value: T | null;
  canEdit?: boolean;
  mode?: Mode;

  // default mode
  renderValue?: (value: T | null) => ReactNode;
  input?: (props: {
    value: T | null;
    onChange: (value: T | null) => void;
    disabled: boolean;
  }) => ReactNode;

  // checkbox mode
  checked?: boolean;
  onToggle?: (checked: boolean) => Promise<boolean>;

  // shared
  onSave?: (value: T) => Promise<boolean>;
}

export function InlineEditableCell<T extends ReactNode>({
  value,
  canEdit = true,
  mode = 'default',

  renderValue,
  input,

  checked,
  onToggle,

  onSave,
}: InlineEditableCellProps<T>) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<T | null>(value);
  const [loading, setLoading] = useState(false);
  const [localChecked, setLocalChecked] = useState<boolean>(!!checked);

  useEffect(() => {
    setLocalChecked(!!checked);
  }, [checked]);

  if (mode === 'checkbox') {
    const toggle = async () => {
      if (!canEdit || loading || !onToggle) return;

      const next = !localChecked;

      setLoading(true);
      const dd = await onToggle(next);
      console.log("got", dd);
      if (dd) setLocalChecked(next);
      setLoading(false);

    };


    return (
      <div className="flex justify-center items-center">
        {loading ? (
          <svg
            className="w-4 h-4 animate-spin text-gray-500"
            viewBox="0 0 24 24"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
          </svg>
        ) : (
          <input
            type="checkbox"
            checked={localChecked}
            disabled={!canEdit || loading}
            onClick={(e) => {
              e.preventDefault();
              toggle();
            }}
          />

        )}
      </div>
    );
  }


  const startEdit = () => {
    if (!canEdit || loading) return;
    setDraft(value);
    setIsEditing(true);
  };

  const cancel = () => {
    if (loading) return;
    setDraft(value);
    setIsEditing(false);
  };

  const save = async () => {
    if (loading || !onSave) return;

    if (draft === value) {
      setIsEditing(false);
      return;
    }


   
      setLoading(true);
      const dd =await onSave(draft as T);
      if(dd) setIsEditing(false);
    
      setLoading(false);
    
  };

  if (!isEditing) {
    return (
      <div
        className={`text-center ${canEdit ? 'cursor-pointer' : ''}`}
        onClick={startEdit}
      >
        {renderValue ? renderValue(value) : value ?? '-'}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-1">
      {input?.({
        value: draft,
        onChange: setDraft,
        disabled: loading,
      })}

      {loading ? (
        <svg className="w-4 h-4 animate-spin text-gray-500" viewBox="0 0 24 24">
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
        </svg>
      ) : (
        <>
          <button onClick={save} className="text-green-600 text-lg">✓</button>
          <button
            onClick={cancel}
            className="text-red-600 text-xs leading-none"
          >
            ✕
          </button>
        </>
      )}
    </div>
  );
}

export default InlineEditableCell;
